// Phase JA.3 — Yesterday's Results.
//
// Renders settled signals for yesterday. Each row composes LegRow with
// a green/red/ghost Badge for the outcome, the kickoff time and a
// conviction Stat.
//
// Doctrine:
//   - No fabrication. When yesterday has no settled rows, surface the
//     honest empty state — never invent placeholders.

import { Badge, Eyebrow, HairlineRow, LegRow, Stat } from "../super-acca";
import type { DailyAccaSignal, SignalOutcome } from "../../free-tier/useDailyAccaSignals.ts";

export interface YesterdayResultsProps {
  rows: DailyAccaSignal[];
  loading?: boolean;
}

function outcomeBadge(o: SignalOutcome): JSX.Element {
  if (o === "won") return <Badge variant="success">✓ WON</Badge>;
  if (o === "lost") return <Badge variant="danger">✗ LOST</Badge>;
  if (o === "void") return <Badge variant="ghost">— VOID</Badge>;
  return <Badge variant="ghost">PENDING</Badge>;
}

function fmtKickoff(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  return `${hh}:${mm} UTC`;
}

export function YesterdayResults({
  rows,
  loading,
}: YesterdayResultsProps): JSX.Element {
  const wonCount = rows.filter((r) => r.outcome === "won").length;
  const settled = rows.filter((r) => r.outcome === "won" || r.outcome === "lost").length;

  return (
    <section
      aria-label="Yesterday's Results"
      data-testid="yesterday-results"
      style={{
        margin: "0 auto",
        maxWidth: 1180,
        padding: "12px 48px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 14,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        <Eyebrow>Yesterday · Settled</Eyebrow>
        {settled > 0 ? (
          <Stat
            label="Hit Rate"
            value={`${wonCount}/${settled}`}
            sub={`${Math.round((wonCount / settled) * 100)}%`}
          />
        ) : null}
      </div>

      {loading ? (
        <div
          style={{
            padding: 14,
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
            fontFamily: "var(--sa-font-mono)",
            fontSize: 11,
            letterSpacing: "0.22em",
            color: "var(--sa-fg-muted)",
            textTransform: "uppercase",
          }}
        >
          Loading yesterday&apos;s settlement…
        </div>
      ) : rows.length === 0 ? (
        <div
          style={{
            padding: 14,
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
            fontFamily: "var(--sa-font-body)",
            fontSize: 13,
            color: "var(--sa-fg-secondary)",
          }}
        >
          Yesterday&apos;s settlement runs at 23:30 WAT — results land here at 00:00 today.
        </div>
      ) : (
        <div
          style={{
            border: "1px solid var(--sa-hairline)",
            borderRadius: 4,
            overflow: "hidden",
          }}
        >
          {rows.map((r, i) => (
            <div key={r.signal_id} data-testid="yesterday-result-row">
              <LegRow
                index={i + 1}
                market={r.market}
                selection={`${r.selection} · ${r.fixture_name}`}
                odds={r.odds}
                confidence={r.conviction_score}
                category={r.tier === "tier_a" ? "ANCHOR" : undefined}
              />
              <HairlineRow padY={10} style={{ padding: "10px 18px" }}>
                {outcomeBadge(r.outcome)}
                <span
                  style={{
                    fontFamily: "var(--sa-font-mono)",
                    fontSize: 11,
                    color: "var(--sa-fg-muted)",
                    letterSpacing: "0.14em",
                  }}
                >
                  KO {fmtKickoff(r.kickoff_at)}
                </span>
                <span style={{ flex: 1 }} />
                <Stat
                  label="Conviction"
                  value={r.conviction_score !== null ? r.conviction_score.toFixed(0) : "—"}
                />
              </HairlineRow>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
